import { ReactNode, MouseEventHandler } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { themed, ITheme } from '../../themer';
import { ArrowForwardIcon, CloseIcon } from '../Icon';
import { ContentWrap } from '../Layout';
import { Css, Responsive } from '../../utils';
import { isRelativeUrl } from '../Link';
import { RouterLink } from '../../../components/common/link/RouterLink';

interface IStyledAlertBannerProps {
	hasCloseButton: boolean;
	isFullyClickable: boolean;
}

export const StyledAlertBanner = styled.div<IStyledAlertBannerProps, ITheme>`
	background: ${themed(({ alertBanner }) => alertBanner.background)};
	padding: 16px 0;

	p:last-of-type {
		margin-bottom: 0;
	}

	${({ isFullyClickable, theme }) =>
		isFullyClickable &&
		css`
			cursor: pointer;
			${StyledAlertBannerLink} {
				color: ${theme.button.textButton.font.color};
			}
			&:hover {
				${StyledAlertBannerLink} {
					color: ${theme.button.textButton.hover.font.color};
					${ArrowForwardIcon} {
						path {
							fill: ${theme.button.textButton.hover.font.color};
						}
					}
				}
			}
		`}

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		padding: 8px 0;

		${ContentWrap} {
			margin-top: 0;
			margin-bottom: 0;
		}

		${({ hasCloseButton }) =>
			hasCloseButton &&
			`
			${StyledAlertBannerTitle} {
				margin-top: 1em;
			}
		`}
	}
`;

export const StyledAlertBannerHeader = styled.div`
	display: flex;
	flex-direction: row;
	align-items: flex-start;

	svg {
		height: 24px;
		margin-top: ${Css.pxToRem(4)};
	}
`;

export const StyledAlertBannerTitle = styled.h5<{}, ITheme>`
	font-size: ${themed(({ alertBanner }) => alertBanner.title.fontSize)};
	flex-grow: 1;
	font-family: ${themed(({ alertBanner }) => alertBanner.title.fontFamily)};
	margin-left: ${Css.pxToRem(8)};
	margin-bottom: ${Css.pxToRem(4)};
`;

const StyledAlertBannerCloseButton = styled.div<{}, ITheme>`
	flex-shrink: 0;
	cursor: pointer;

	${CloseIcon} {
		max-width: 24px;
	}

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		span {
			display: none;
			font-weight: bold;
		}
	}
`;

interface IAlertBannerProps {
	title: string;
	children: ReactNode;
	closeLabel?: string;
	onClickClose?: MouseEventHandler;
	alertIcon?: ReactNode;
	fullyClickable?: boolean;
	url?: string;
}

export function AlertBanner({
	fullyClickable = false,
	url,
	title,
	children,
	closeLabel,
	onClickClose,
	alertIcon,
}: IAlertBannerProps) {
	return fullyClickable && url ? (
		<StyledLink href={url} target={!isRelativeUrl(url) ? '_blank' : undefined}>
			<StyledAlertBanner
				isFullyClickable={fullyClickable}
				hasCloseButton={onClickClose !== undefined}
				role="alert"
			>
				<ContentWrap>
					<StyledAlertBannerHeader>
						{alertIcon && alertIcon}
						<StyledAlertBannerTitle>{title}</StyledAlertBannerTitle>
						{onClickClose && (
							<StyledAlertBannerCloseButton onClick={onClickClose}>
								{closeLabel && <span>{closeLabel}</span>}
								<CloseIcon />
							</StyledAlertBannerCloseButton>
						)}
					</StyledAlertBannerHeader>
					{children}
					<AlertFooterWrap>
						<StyledAlertBannerLink>
							Read more <ArrowForwardIcon aria-label="Forward" />
						</StyledAlertBannerLink>
					</AlertFooterWrap>
				</ContentWrap>
			</StyledAlertBanner>
		</StyledLink>
	) : (
		<StyledAlertBanner
			isFullyClickable={fullyClickable}
			hasCloseButton={onClickClose !== undefined}
			role="alert"
		>
			<ContentWrap>
				<StyledAlertBannerHeader>
					{alertIcon && alertIcon}
					<StyledAlertBannerTitle>{title}</StyledAlertBannerTitle>
					{onClickClose && (
						<StyledAlertBannerCloseButton onClick={onClickClose}>
							{closeLabel && <span>{closeLabel}</span>}
							<CloseIcon />
						</StyledAlertBannerCloseButton>
					)}
				</StyledAlertBannerHeader>
				{children}
			</ContentWrap>
		</StyledAlertBanner>
	);
}

const StyledLink = styled(RouterLink)`
	text-decoration: none;
	display: flex;
	flex-direction: column;
	flex-grow: 1;
`;

const AlertFooterWrap = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: flex-end;
	margin-top: ${Css.pxToRem(8)};
`;

const StyledAlertBannerLink = styled.div`
	display: flex;
	flex-direction: row;
	gap: ${Css.pxToRem(8)};
	align-items: center;
`;
